"use client";

import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { gsap } from "gsap";
import Link from "next/link";
import { ArrowLeft, Loader2, Mail } from "lucide-react";
import { MarketingFooter } from "@/components/marketing/MarketingFooter";
import { PASSWORD_MIN_LENGTH } from "@/lib/auth/password";

const EASE_OUT_EXPO = [0.16, 1, 0.3, 1] as const;

const slideVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -12 },
};

type FormStatus = "idle" | "loading" | "success";

export default function ForgotPasswordPage() {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<FormStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const glowRef = useRef<HTMLDivElement>(null);
  const glowRef2 = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!glowRef.current || !glowRef2.current) return;

    gsap.fromTo(
      glowRef.current,
      { opacity: 0, scale: 0.5 },
      { opacity: 1, scale: 1, duration: 2.8, ease: "power2.out" },
    );

    gsap.fromTo(
      glowRef2.current,
      { opacity: 0, scale: 0.5 },
      { opacity: 0.6, scale: 1, duration: 3.5, ease: "power2.out", delay: 0.3 },
    );
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !email.trim() || status === "loading") return;
    setError(null);
    setStatus("loading");
    try {
      const res = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: username.trim(),
          email: email.trim(),
        }),
      });
      const json = (await res.json()) as {
        success: boolean;
        error?: string;
        data?: { message?: string };
      };
      if (!res.ok || !json.success) {
        setError(json.error ?? "Could not send reset link");
        setStatus("idle");
        return;
      }
      setSuccessMessage(
        json.data?.message ?? "Password reset link sent. Check your inbox.",
      );
      setStatus("success");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
      setStatus("idle");
    }
  };

  const handleTryAgain = () => {
    setUsername("");
    setEmail("");
    setError(null);
    setSuccessMessage(null);
    setStatus("idle");
  };

  return (
    <>
      <main className="min-h-screen w-full flex items-center justify-center bg-[#050505] p-4 relative overflow-hidden">
        <motion.div
          ref={glowRef}
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[640px] h-[640px] rounded-full bg-amber-500/[0.035] blur-[130px]"
        />
        <motion.div
          ref={glowRef2}
          aria-hidden="true"
          className="pointer-events-none absolute top-[30%] left-[45%] w-[300px] h-[300px] rounded-full bg-amber-400/[0.025] blur-[90px]"
        />

        <div className="w-full max-w-[420px] relative z-10">
          <AnimatePresence mode="wait">
            {status !== "success" ? (
              <motion.div
                key="form-state"
                variants={slideVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.5, ease: EASE_OUT_EXPO }}
                className="bg-[#0A0A0A] border border-white/[0.08] rounded-2xl p-8 sm:p-10 shadow-[0_32px_80px_rgba(0,0,0,0.9)]"
              >
                <Link
                  href="/login"
                  data-testid="back-to-login-link"
                  className="inline-flex items-center gap-1.5 text-zinc-500 hover:text-zinc-300 transition-colors duration-200 mb-8 group text-sm"
                >
                  <ArrowLeft
                    size={15}
                    className="transition-transform duration-200 group-hover:-translate-x-0.5"
                    strokeWidth={2}
                  />
                  Back
                </Link>

                <p className="text-[10px] font-semibold tracking-[0.24em] uppercase text-zinc-700 mb-7 select-none">
                  Inora
                </p>

                <h1 className="text-[28px] sm:text-3xl font-light tracking-tight text-white mb-2 leading-tight">
                  Forgot password?
                </h1>
                <p className="text-sm text-zinc-500 leading-relaxed mb-8">
                  Enter your <span className="text-zinc-300">username</span> and
                  the <span className="text-zinc-300">recovery email</span> saved
                  on your account. We&apos;ll send a reset link if they match.
                </p>

                <form onSubmit={handleSubmit} className="space-y-3">
                  <motion.div className="space-y-1.5">
                    <label
                      htmlFor="fp-username"
                      className="block text-xs font-medium text-zinc-300"
                    >
                      Username
                    </label>
                    <input
                      id="fp-username"
                      type="text"
                      data-testid="forgot-password-username-input"
                      autoComplete="username"
                      placeholder="your_username"
                      value={username}
                      onChange={(e) => {
                        setUsername(
                          e.target.value
                            .toLowerCase()
                            .replace(/[^a-z0-9_]/g, ""),
                        );
                        setError(null);
                      }}
                      required
                      minLength={3}
                      className="w-full rounded-xl bg-black border border-white/[0.08] px-4 py-3 text-sm text-white placeholder:text-zinc-700 outline-none focus:border-amber-500/40 focus:ring-2 focus:ring-amber-500/10 transition-all duration-200"
                    />
                  </motion.div>

                  <motion.div className="space-y-1.5">
                    <label
                      htmlFor="fp-email"
                      className="block text-xs font-medium text-zinc-300"
                    >
                      Recovery email
                    </label>
                    <input
                      id="fp-email"
                      type="email"
                      data-testid="forgot-password-email-input"
                      autoComplete="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setError(null);
                      }}
                      required
                      className="w-full rounded-xl bg-black border border-white/[0.08] px-4 py-3 text-sm text-white placeholder:text-zinc-700 outline-none focus:border-amber-500/40 focus:ring-2 focus:ring-amber-500/10 transition-all duration-200"
                    />
                  </motion.div>

                  <p className="text-[11px] text-zinc-600 leading-relaxed">
                    No recovery email yet? Add one in Account Settings while
                    signed in, or use Change password there if you still have
                    access.
                  </p>

                  {error && (
                    <p className="text-[12px] text-red-400 bg-red-950/20 border border-red-900/40 rounded-lg px-3 py-2">
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    data-testid="send-reset-button"
                    disabled={
                      !username.trim() || !email.trim() || status === "loading"
                    }
                    className="w-full rounded-xl bg-amber-500 hover:bg-amber-400 active:scale-[0.98] disabled:opacity-30 disabled:pointer-events-none text-black font-semibold text-sm py-3 transition-all duration-200 flex items-center justify-center gap-2"
                  >
                    {status === "loading" ? (
                      <>
                        <Loader2
                          size={14}
                          className="animate-spin"
                          strokeWidth={2.5}
                        />
                        Sending&hellip;
                      </>
                    ) : (
                      "Send reset link"
                    )}
                  </button>
                </form>

                <p className="text-center text-xs text-zinc-700 mt-7">
                  Remembered it?{" "}
                  <Link
                    href="/login"
                    className="text-zinc-400 hover:text-white transition-colors duration-200"
                  >
                    Sign in
                  </Link>
                </p>
              </motion.div>
            ) : (
              <motion.div
                key="success-state"
                variants={slideVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.5, ease: EASE_OUT_EXPO }}
                className="bg-[#0A0A0A] border border-white/[0.08] rounded-2xl p-8 sm:p-10 shadow-[0_32px_80px_rgba(0,0,0,0.9)] text-center"
              >
                <motion.div
                  initial={{ scale: 0.7, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.45, ease: EASE_OUT_EXPO, delay: 0.1 }}
                  className="mx-auto mb-6 w-[68px] h-[68px] rounded-2xl bg-amber-500/[0.06] border border-amber-500/20 flex items-center justify-center"
                >
                  <Mail size={30} className="text-amber-500" strokeWidth={1.5} />
                </motion.div>

                <h1 className="text-2xl font-light tracking-tight text-white mb-3">
                  Check your email
                </h1>
                <p className="text-sm text-zinc-500 leading-relaxed mb-8 max-w-[300px] mx-auto">
                  {successMessage ?? "Password reset link sent. Check your inbox."}{" "}
                  The link expires in{" "}
                  <span className="text-zinc-300 font-medium">15 minutes</span>.
                </p>

                <p className="text-xs text-zinc-600 mb-6 leading-relaxed">
                  Didn&apos;t receive it? Check spam or{" "}
                  <button
                    type="button"
                    onClick={handleTryAgain}
                    className="text-zinc-400 hover:text-white transition-colors duration-200 underline-offset-2 hover:underline"
                  >
                    try again
                  </button>
                  . Password must be at least {PASSWORD_MIN_LENGTH} characters.
                </p>

                <Link
                  href="/login"
                  data-testid="back-to-login-link"
                  className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors duration-200"
                >
                  Back to login
                </Link>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
      <MarketingFooter className="bg-[#050505]" />
    </>
  );
}
